/**
 * 翻譯建議：以繁中主稿產生英日草稿，逐格挑要不要採用。
 *
 * ## 為什麼是「建議」而不是直接寫進去
 *
 * 機器翻譯一定會有幾格是錯的——地名被意譯、標題被翻成一整句、日文敬語用錯層級。
 * 一旦直接寫入，它就與人工填的長得一模一樣，之後沒有人分得出哪幾格需要重看。所以
 * 這裡只把譯文放進**表單**，仍然要按「儲存變更」才進資料庫，而且採用的格子會被標記
 * 成「機翻待校」（見總覽分頁的黃色格子）。
 *
 * 推薦點是唯一的例外：那個區塊本來就是即時儲存（卡片失焦就送出），沒有表單可以
 * 暫存，所以採用當下就寫進去——但同樣帶著「機翻待校」記號。詳見
 * `spot-poi-translation.ts`。
 *
 * ## 字數要看得見
 *
 * 窗屏版面是固定的，英日譯文普遍比中文長。表格上直接列出「原文 12 字 → 譯文 47 字」，
 * 太長的當場就看得出來，不必等發布到船上才發現標題被截斷。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from './Button';
import { DISPLAY_FIELDS } from './SpotDisplayFields';
import { buildPoiApplyPlan, isPoiItem } from './spot-poi-translation';
import {
  listSpotPois,
  POI_CATEGORY_LABEL,
  translateSpot,
  upsertSpotPoi,
  type SpotTranslateItem,
  type SpotTranslateResult,
  type TranslateTargetLocale,
} from '../lib/api-client';

/**
 * 欄位名（`poi_title_i18n`）→ 人看得懂的標籤。
 *
 * 基本資料那兩欄不在 `DISPLAY_FIELDS` 裡（那張表只有窗屏文字），要另外補上，
 * 否則畫面上會直接露出 `name_i18n` 這種鍵名。推薦點的鍵帶 `poi.` 前綴，與景點的
 * 同名欄位（`name_i18n`／`intro_i18n`）刻意分開。
 */
const FIELD_LABEL: Record<string, string> = {
  name_i18n: '景點名稱',
  description_i18n: '景點描述（HTML）',
  ...Object.fromEntries(DISPLAY_FIELDS.map((f) => [`${f.key}_i18n`, f.label])),
  'poi.name_i18n': '店名',
  'poi.nearby_i18n': '所在地',
  'poi.intro_i18n': '簡介',
  'poi.hours_i18n': '營業時間',
};

const LOCALE_LABEL: Record<string, string> = { en: 'EN', ja: '日本語' };

/** 譯文比原文長這個倍數以上就標出來——窗屏版面固定，長太多會被截掉。 */
const LENGTH_WARN_RATIO = 2;

export interface AppliedTranslation {
  field: string;
  locale: TranslateTargetLocale;
  text: string;
}

interface Props {
  spotId: string;
  /** 套用勾選的譯文到表單（不寫資料庫，仍需按儲存變更）。 */
  onApply: (items: readonly AppliedTranslation[]) => void;
}

/**
 * 勾選狀態的鍵。
 *
 * 必須帶上推薦點的 id：20 家店的「店名（EN）」欄位鍵完全一樣，少了這一段會變成
 * 勾一家等於勾全部。
 */
function keyOf(item: { field: string; locale: string; poi?: { id: string } }): string {
  return `${item.poi?.id ?? ''}|${item.field}|${item.locale}`;
}

/** 推薦點的建議要標出是哪一家店；景點自己的欄位不需要。 */
function subLabel(item: { poi?: { category: string; seq: number; label: string } }): string | null {
  if (!item.poi) return null;
  const category = POI_CATEGORY_LABEL[item.poi.category as keyof typeof POI_CATEGORY_LABEL];
  return `${category ?? item.poi.category} #${item.poi.seq} ${item.poi.label}`;
}

/** 採用的結果：填進表單幾格、直接寫進推薦點幾格。 */
interface AppliedCount {
  form: number;
  poi: number;
  orphaned: number;
}

export function SpotTranslatePanel({ spotId, onApply }: Props): JSX.Element {
  const qc = useQueryClient();
  const [overwrite, setOverwrite] = useState(false);
  const [result, setResult] = useState<SpotTranslateResult | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState<AppliedCount | null>(null);

  // 推薦點的譯文要併進**目前伺服器上的** i18n 物件（整包取代語意），所以要先有這份
  const poiQuery = useQuery({
    queryKey: ['spot.pois', spotId],
    queryFn: () => listSpotPois(spotId),
  });
  const poiList = Object.values(poiQuery.data?.categories ?? {}).flat();

  const run = useMutation({
    mutationFn: () => translateSpot(spotId, { overwrite }),
    onSuccess: (r) => {
      setResult(r);
      // 預設全選：使用者要的是「翻一翻」，逐格勾選是例外而不是常態
      setPicked(new Set(r.items.map(keyOf)));
      setApplied(null);
    },
  });

  const toggle = (item: SpotTranslateItem): void => {
    setPicked((prev) => {
      const next = new Set(prev);
      const k = keyOf(item);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  /**
   * 採用：景點欄位填進表單，推薦點直接寫進去。
   *
   * 兩條路的差別在於原本的儲存語意（見檔頭）。推薦點那邊是逐筆 PUT 而不是一次
   * 全送——沒有批次端點，而一筆一個請求至少能在中途失敗時把已成功的那幾筆留住。
   */
  const applyMutation = useMutation({
    mutationFn: async (): Promise<AppliedCount> => {
      const chosen = (result?.items ?? []).filter((i) => picked.has(keyOf(i)));

      const formItems = chosen.filter((i) => !isPoiItem(i));
      onApply(
        formItems.map((i) => ({
          field: i.field,
          locale: i.locale as TranslateTargetLocale,
          text: i.translated_text,
        })),
      );

      const plan = buildPoiApplyPlan(chosen.filter(isPoiItem), poiList);
      for (const target of plan.targets) {
        await upsertSpotPoi(spotId, target.category, target.seq, target.patch);
      }
      return {
        form: formItems.length,
        poi: plan.targets.reduce((n, t) => n + t.cells, 0),
        orphaned: plan.orphaned,
      };
    },
    onSuccess: (count) => {
      setApplied(count);
      if (count.poi > 0) void qc.invalidateQueries({ queryKey: ['spot.pois', spotId] });
    },
  });

  return (
    <div style={{ margin: '12px 0' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="primary" onClick={() => run.mutate()} disabled={run.isPending}>
          {run.isPending ? '翻譯中…' : '🌐 產生英日翻譯建議'}
        </Button>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => {
              setOverwrite(e.target.checked);
              setResult(null);
            }}
          />
          連已有譯文的欄位也重翻
        </label>
      </div>

      <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0', maxWidth: 900 }}>
        以<strong>繁中為主稿</strong>翻成英文與日文，涵蓋<strong>基本資料</strong>
        （名稱、描述）、<strong>窗屏文字</strong>六欄，以及<strong>推薦點</strong>
        的店名、所在地、簡介與營業時間。景點欄位只會填進下方的表單，
        <strong>仍要按「儲存變更」才寫入</strong>；推薦點本來就是即時儲存，採用當下
        就寫進去。兩者採用的格子都會標成「機翻待校」，在總覽分頁顯示為黃色，人校對過
        再解除。語音提示與 ASR 詞表不在翻譯範圍內（那是給語音辨識用的諧音提示， 翻了沒有用途）。
      </p>

      {(run.error ?? applyMutation.error ?? poiQuery.error) && (
        <div className="error-banner" style={{ marginTop: 8 }}>
          {((run.error ?? applyMutation.error ?? poiQuery.error) as Error).message}
        </div>
      )}

      {result && result.model === 'stub' && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            fontSize: 12,
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 4,
          }}
        >
          ⚠ 目前連的是測試用的假翻譯供應商（<code>stub</code>），譯文只是把原文加上語言標記。
        </div>
      )}

      {applied !== null && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            fontSize: 12,
            background: '#ecfdf5',
            border: '1px solid #6ee7b7',
            borderRadius: 4,
          }}
        >
          {applied.form > 0 && (
            <>
              ✓ 已把 {applied.form} 格譯文填進表單。
              <strong>記得按下方的「儲存變更」</strong>，否則離開這頁就沒了。
            </>
          )}
          {applied.poi > 0 && (
            <div>✓ 推薦點的 {applied.poi} 格已直接儲存（該區塊本來就是即時儲存）。</div>
          )}
          {applied.orphaned > 0 && (
            <div style={{ color: '#b45309' }}>
              ⚠ 有 {applied.orphaned} 格找不到對應的推薦點（產生建議之後被刪除或重排過），
              已跳過。請重新產生建議。
            </div>
          )}
        </div>
      )}

      {result && (
        <>
          {result.items.length === 0 && (
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 10 }}>
              沒有需要翻譯的欄位——中文主稿是空的，或英日都已經有內容（要重翻請勾選上方的覆蓋）。
            </div>
          )}

          {result.items.length > 0 && (
            <>
              <div className="table-card" style={{ margin: '10px 0', maxWidth: 1000 }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 34 }}>
                        <input
                          type="checkbox"
                          checked={picked.size === result.items.length}
                          onChange={(e) =>
                            setPicked(
                              e.target.checked ? new Set(result.items.map(keyOf)) : new Set(),
                            )
                          }
                          title="全選／全不選"
                        />
                      </th>
                      <th style={{ width: 150 }}>欄位</th>
                      <th>繁中主稿</th>
                      <th>譯文建議</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.items.map((item) => (
                      <TranslateRow
                        key={keyOf(item)}
                        item={item}
                        checked={picked.has(keyOf(item))}
                        onToggle={() => toggle(item)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <Button
                variant="primary"
                onClick={() => applyMutation.mutate()}
                disabled={picked.size === 0 || applyMutation.isPending}
              >
                {applyMutation.isPending ? '套用中…' : `套用勾選的 ${picked.size} 格`}
              </Button>
            </>
          )}

          {result.skipped.length > 0 && (
            <details style={{ marginTop: 10, fontSize: 12 }}>
              <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
                沒有翻的 {result.skipped.length} 格
              </summary>
              <div style={{ marginTop: 4, color: '#6b7280' }}>
                {result.skipped.map((s) => (
                  <div key={keyOf(s)} style={{ padding: '2px 0' }}>
                    {subLabel(s) && <span style={{ color: '#9ca3af' }}>{subLabel(s)} · </span>}
                    {FIELD_LABEL[s.field] ?? s.field}（{LOCALE_LABEL[s.locale] ?? s.locale}）—{' '}
                    {s.reason}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}

/**
 * 表格裡只給前 200 字。
 *
 * 描述欄是 HTML，整段貼出來會把版面撐爆而且滿是標籤；**刻意以原始碼顯示而不是
 * 渲染**——這裡要確認的是「標籤有沒有被翻掉或弄亂」，渲染出來反而看不見那件事。
 */
function preview(value: string): string {
  return value.length > 200 ? `${value.slice(0, 200)}…` : value;
}

function TranslateRow({
  item,
  checked,
  onToggle,
}: {
  item: SpotTranslateItem;
  checked: boolean;
  onToggle: () => void;
}): JSX.Element {
  const tooLong = item.translated_chars > item.source_chars * LENGTH_WARN_RATIO;
  return (
    <tr>
      <td>
        <input type="checkbox" checked={checked} onChange={onToggle} />
      </td>
      <td style={{ fontSize: 13 }}>
        {subLabel(item) && (
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{subLabel(item)}</div>
        )}
        {FIELD_LABEL[item.field] ?? item.field}
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          {LOCALE_LABEL[item.locale] ?? item.locale}
          {item.existing_value !== null && (
            <span style={{ color: '#b45309' }}> · 會取代既有內容</span>
          )}
        </div>
      </td>
      <td style={{ fontSize: 12, whiteSpace: 'pre-wrap', maxWidth: 320 }}>
        {preview(item.source_text)}
        <div style={{ color: '#9ca3af' }}>{item.source_chars} 字</div>
      </td>
      <td style={{ fontSize: 12, whiteSpace: 'pre-wrap', maxWidth: 380 }}>
        {preview(item.translated_text)}
        <div style={{ color: tooLong ? '#b45309' : '#9ca3af' }}>
          {item.translated_chars} 字{tooLong && ' ⚠ 比原文長很多，窗屏可能放不下'}
        </div>
        {item.existing_value !== null && (
          <div style={{ color: '#b45309', marginTop: 2 }}>現有：{item.existing_value}</div>
        )}
      </td>
    </tr>
  );
}
